"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, CarouselWrapper, type CarouselApi } from "@/components/ui/carousel/carouselItems";
import { Image, type ImageProps } from "@/components/ui/Image";
import { IconButton } from "@/components/ui/buttons/IconButton";
import { useTranslations } from "@/lib/i18n/useTranslations";
function useArrowKeyNavigation(api, isOpen) {
  const $ = _c(4);
  let t0;
  let t1;
  if ($[0] !== api || $[1] !== isOpen) {
    t0 = () => {
      if (!isOpen || !api) {
        return;
      }
      const navigate = event => {
        if (event.defaultPrevented) {
          return;
        }
        if (event.key === "ArrowLeft") {
          api.scrollPrev();
        } else {
          if (event.key === "ArrowRight") {
            api.scrollNext();
          }
        }
      };
      window.addEventListener("keydown", navigate);
      return () => window.removeEventListener("keydown", navigate);
    };
    t1 = [api, isOpen];
    $[0] = api;
    $[1] = isOpen;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  React.useEffect(t0, t1);
}
export interface LightboxProps {
  /**
   * Images of the group, in the order they should be navigated
   */
  images: ImageProps[];

  /**
   * Index of the image to show first
   */
  startIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}
export const Lightbox: React.FC<LightboxProps> = t0 => {
  const $ = _c(25);
  const {
    images,
    startIndex,
    open,
    onOpenChange
  } = t0;
  const {
    gallery: t
  } = useTranslations();
  const [api, setApi] = React.useState();
  useArrowKeyNavigation(api, open);
  let t1;
  if ($[0] !== t.lightboxTitle) {
    t1 = <DialogTitle className="sr-only">{t.lightboxTitle}</DialogTitle>;
    $[0] = t.lightboxTitle;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== startIndex) {
    t2 = {
      startIndex,
      loop: false
    };
    $[2] = startIndex;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== images) {
    t3 = images.map(_temp);
    $[4] = images;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <CarouselContent>{t3}</CarouselContent>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="absolute bottom-md left-1/2 z-10 flex -translate-x-1/2 gap-md md:contents"><CarouselPrevious className="z-10 max-md:static max-md:translate-y-0! md:absolute md:top-1/2 md:left-lg! md:-translate-y-1/2!" /><CarouselNext className="z-10 max-md:static max-md:translate-y-0! md:absolute md:top-1/2 md:right-lg! md:-translate-y-1/2!" /></div>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t2 || $[10] !== t4) {
    t6 = <CarouselWrapper className="relative size-full" variant="light" setApi={setApi} opts={t2}>{t4}{t5}</CarouselWrapper>;
    $[9] = t2;
    $[10] = t4;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== onOpenChange) {
    t7 = () => onOpenChange(false);
    $[12] = onOpenChange;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t.close || $[15] !== t7) {
    t8 = <IconButton icon="close" color="dark" label={t.close} className="absolute top-md md:top-lg right-md md:right-lg z-10 bg-transparent" onClick={t7} />;
    $[14] = t.close;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t1 || $[18] !== t6 || $[19] !== t8) {
    t9 = <DialogContent aria-describedby={undefined} overlayClassName="bg-black/60 backdrop-blur-sm">{t1}{t6}{t8}</DialogContent>;
    $[17] = t1;
    $[18] = t6;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== onOpenChange || $[22] !== open || $[23] !== t9) {
    t10 = <Dialog open={open} onOpenChange={onOpenChange}>{t9}</Dialog>;
    $[21] = onOpenChange;
    $[22] = open;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
};
function _temp(image, index) {
  return <CarouselItem key={`${image.src}-${index}`} className="h-dvh basis-full"><div className="flex size-full items-center justify-center p-md max-md:pb-23 md:p-3xl"><Image {...image} objectFit="contain" rounded="md" sizes="100vw" quality={90} className="max-h-full w-auto max-w-full" lightboxIndex={undefined} /></div></CarouselItem>;
}